import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import API from '../api/axios';
import { useAuth } from './AuthContext';

const UserPreferencesContext = createContext();

export function UserPreferencesProvider({ children }) {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) {
      setPreferences({});
      setLoaded(false);
      return;
    }
    let cancelled = false;
    API.get('/settings/preferences')
      .then(({ data }) => {
        if (!cancelled) {
          setPreferences(data || {});
          setLoaded(true);
        }
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [user]);

  const savePreferences = useCallback(async (next) => {
    setPreferences(next);
    await API.put('/settings/preferences', { preferences: next });
  }, []);

  const hiddenPages = useMemo(() => preferences.hiddenPages || [], [preferences]);

  const isPageHidden = useCallback((path) => hiddenPages.includes(path), [hiddenPages]);

  const togglePageHidden = useCallback(async (path) => {
    const next = hiddenPages.includes(path)
      ? hiddenPages.filter((p) => p !== path)
      : [...hiddenPages, path];
    await savePreferences({ ...preferences, hiddenPages: next });
  }, [hiddenPages, preferences, savePreferences]);

  return (
    <UserPreferencesContext.Provider
      value={{ preferences, loaded, hiddenPages, isPageHidden, togglePageHidden, savePreferences }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences() {
  return useContext(UserPreferencesContext);
}
